import { Link, useLocation } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import type { NavGroupApi, NavItemApi } from '~/types/navigation.types';
import { cn } from '~/utils/cn';

/** Sub-item with its icon pre-resolved by the parent to satisfy react-hooks/static-components. */
export type SubmenuItem = Omit<NavItemApi, 'items'> & {
  /** Resolved LucideIcon, or null when the API item carries no icon. */
  Icon: LucideIcon | null;
};

interface NavGroupWithSubmenuProps {
  group: NavGroupApi;
  /** Pre-resolved group icon — caller resolves via `getIcon` so this component
   *  never creates a component during its own render. */
  GroupIcon: LucideIcon;
  /** Sub-items annotated with their pre-resolved icon, same reason. */
  items: SubmenuItem[];
  /** When true the rail is expanded: render the group label inline beside the
   *  icon (the hover flyout of sub-items still opens to the right). */
  expanded?: boolean;
}

/**
 * Collapsed sidebar icon for a nav group that has sub-items.
 * Hovering opens a DropdownMenu anchored to the right of the icon;
 * clicking a sub-item navigates to that path.
 *
 * Hover handlers live on the wrapper <div> — a plain DOM element with no
 * Radix/Slot layers. A 200 ms close timer lets the cursor travel from the
 * icon into the floating panel without the panel closing mid-transition.
 * The exit animation is disabled so a mid-travel close produces no visible
 * flash before the panel reopens.
 */
export function NavGroupWithSubmenu({
  group,
  GroupIcon,
  items,
  expanded = false,
}: NavGroupWithSubmenuProps) {
  const { pathname } = useLocation();

  const isGroupActive = items.some(
    (item) => pathname === item.path || pathname.startsWith(item.path + '/'),
  );

  const [open, setOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openMenu = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setOpen(true);
  };

  const scheduleClose = () => {
    // 200 ms gives the cursor enough time to cross the gap between the icon
    // and the floating panel without accidentally triggering a close.
    closeTimerRef.current = setTimeout(() => {
      setOpen(false);
    }, 200);
  };

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  return (
    <div onMouseEnter={openMenu} onMouseLeave={scheduleClose}>
      {/*
       * modal={false} disables Radix's DismissableLayer and focus trap so the
       * dropdown does not auto-close when focus shifts during the hover
       * transition. Open/close is controlled exclusively by the hover timer.
       * onOpenChange only allows Radix to open the menu (e.g. via keyboard);
       * closing is handled by scheduleClose so pointer-outside events from
       * Radix don't race against the hover timer and cause a flicker.
       */}
      <DropdownMenu
        open={open}
        onOpenChange={(next) => {
          if (next) setOpen(true);
        }}
        modal={false}
      >
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={group.title}
            className="group/nav flex w-full cursor-pointer items-center text-sm font-medium"
          >
            {/* Fixed 72px icon zone — keeps the icon in the same x-position
                whether the rail is collapsed or expanded. */}
            <span className="flex w-18 shrink-0 items-center justify-center py-0.5">
              <span
                className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-xl transition-colors',
                  isGroupActive
                    ? '[&_svg]:text-nav-active shadow-nav-active bg-white'
                    : '[&_svg]:text-nav-icon group-hover/nav:bg-white/60',
                )}
              >
                <GroupIcon aria-hidden="true" className="h-5 w-5 shrink-0" />
              </span>
            </span>
            {expanded && (
              <>
                <span
                  className={cn(
                    'flex-1 truncate text-left',
                    isGroupActive ? 'text-nav-active' : 'text-nav-foreground',
                  )}
                >
                  {group.title}
                </span>
                <ChevronRight
                  aria-hidden="true"
                  className="text-nav-icon mr-3 h-4 w-4 shrink-0 opacity-60"
                />
              </>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side="right"
          align="start"
          sideOffset={6}
          className="w-48 data-[state=closed]:animate-none"
          onMouseEnter={openMenu}
          onMouseLeave={scheduleClose}
        >
          <DropdownMenuLabel className="text-muted-foreground text-xs font-medium">
            {group.title}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {items.map((item) => {
            const { Icon } = item;
            const isActive = pathname === item.path || pathname.startsWith(item.path + '/');
            return (
              <DropdownMenuItem key={item.id} asChild>
                <Link
                  to={item.path}
                  aria-current={isActive ? 'page' : undefined}
                  className={cn(
                    'cursor-pointer',
                    isActive && 'font-medium',
                    isActive && 'text-primary',
                  )}
                  onClick={() => {
                    setOpen(false);
                  }}
                >
                  {Icon && <Icon aria-hidden="true" className="h-4 w-4" />}
                  <span>{item.title}</span>
                </Link>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
